import { Controller, useFormContext } from 'react-hook-form';
import { Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import { Typography } from '@shinetools/sunshine-universal';
import { isNil } from 'ramda';

import * as Form from 'components/_core/form';
import { INTERCOM_LAUNCHER_PROPS } from 'features/Intercom';
import { GENDER_OPTIONS } from 'features/Onboarding/modules/Identity/libs/constants';

import Callout from '../../../../../components/_core/Callout';
import { BeneficiarySchema } from '../beneficiarySchema';
import locales from '../locales';

interface RequiredFieldsProps {
  isInvalidBeneficiary: boolean;
  roleWarningCalloutContext?: 'create' | 'edit';
}

const radioTitleMinWidth = '60px';
const RequiredFields = ({
  isInvalidBeneficiary,
  roleWarningCalloutContext,
}: RequiredFieldsProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const {
    control,
    formState: { errors },
    register,
  } = useFormContext<BeneficiarySchema>();

  return (
    <>
      <Controller
        control={control}
        name="gender"
        render={({ field: { onChange, value } }) => (
          <Form.Field
            error={errors.gender?.message}
            label={locales.genderLabel}
            marginBottom="space-32"
          >
            <Form.Radio.Group
              alignSelf="flex-start"
              direction="row"
              name="radio-group"
              onChange={onChange}
              value={value ?? undefined}
              variant="outline"
            >
              {GENDER_OPTIONS.map((option) => (
                <Form.Radio key={option.value} value={option.value}>
                  <Form.Radio.Title minWidth={radioTitleMinWidth}>
                    {option.abbreviatedLabel}
                  </Form.Radio.Title>
                </Form.Radio>
              ))}
            </Form.Radio.Group>
          </Form.Field>
        )}
      />
      <Stack
        direction={isMobile ? 'column' : 'row'}
        marginBottom="space-32"
        spacing={isMobile ? 'space-32' : 'space-8'}
      >
        <Form.Field
          error={errors.firstName?.message}
          label={locales.firstNameLabel}
        >
          <Form.Input {...register('firstName')} />
        </Form.Field>

        <Form.Field
          error={errors.lastName?.message}
          label={locales.lastNameLabel}
        >
          <Form.Input {...register('lastName')} />
        </Form.Field>
      </Stack>

      <Callout hideIcon marginBottom="space-32">
        <Callout.Content>
          {`${locales.helpPrompt} `}
          <Typography.Link
            forceIcon={false}
            size="small"
            {...INTERCOM_LAUNCHER_PROPS}
          >
            {locales.helpLink}
          </Typography.Link>
        </Callout.Content>
      </Callout>

      <Controller
        control={control}
        name="isDirector"
        render={({ field: { onChange, value } }) => (
          <Form.Field
            error={errors.isDirector?.message}
            marginBottom="space-32"
          >
            <Text fontWeight="weight-500" variant="primary">
              {locales.directorLabel.title}
            </Text>
            <Text marginBottom="space-16">
              {locales.directorLabel.description}
            </Text>
            <Form.Radio.Group
              direction="row"
              onChange={(val) => onChange(val === 'true')}
              radioPosition="right"
              value={isNil(value) ? undefined : String(value)}
              variant="outline"
            >
              <Form.Radio value="true">
                <Form.Radio.Title minWidth={radioTitleMinWidth}>
                  {locales.yes}
                </Form.Radio.Title>
              </Form.Radio>

              <Form.Radio value="false">
                <Form.Radio.Title minWidth={radioTitleMinWidth}>
                  {locales.no}
                </Form.Radio.Title>
              </Form.Radio>
            </Form.Radio.Group>
          </Form.Field>
        )}
      />
      <Controller
        control={control}
        name="isMajorShareholder"
        render={({ field: { onChange, value } }) => (
          <Form.Field error={errors.isMajorShareholder?.message}>
            <Text fontWeight="weight-500" variant="primary">
              {locales.shareholderLabel.title}
            </Text>
            <Text marginBottom="space-16">
              {locales.shareholderLabel.description}
            </Text>
            <Form.Radio.Group
              direction="row"
              onChange={(val) => onChange(val === 'true')}
              radioPosition="right"
              value={isNil(value) ? undefined : String(value)}
              variant="outline"
            >
              <Form.Radio value="true">
                <Form.Radio.Title minWidth={radioTitleMinWidth}>
                  {locales.yes}
                </Form.Radio.Title>
              </Form.Radio>

              <Form.Radio value="false">
                <Form.Radio.Title minWidth={radioTitleMinWidth}>
                  {locales.no}
                </Form.Radio.Title>
              </Form.Radio>
            </Form.Radio.Group>
          </Form.Field>
        )}
      />
      {isInvalidBeneficiary ? (
        <Callout marginTop="space-16" variant="warning">
          {roleWarningCalloutContext === 'create'
            ? locales.create.invalidBeneficiary
            : locales.edit.invalidBeneficiary}
        </Callout>
      ) : null}
    </>
  );
};

export default RequiredFields;
